import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { rupiah, num, formatTanggalInput } from '../lib/format';
import { ShoppingBag } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SALURAN = ['Offline', 'Shopee', 'Tokopedia', 'TikTok Shop', 'WhatsApp', 'Lainnya'];
const NO_FEE = ['Offline', 'WhatsApp'];

export default function Penjualan() {
  const [products, setProducts] = useState([]);
  const today = formatTanggalInput(new Date().toISOString());
  const [form, setForm] = useState({ product_id: '', qty: 1, harga_per_unit: '', potongan_persen: 0, tanggal: today, catatan: '', saluran: 'Offline' });

  const load = async () => {
    const p = await api.get('/products');
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const selected = products.find((p) => p.id === form.product_id);

  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    setForm({
      ...form,
      product_id: id,
      harga_per_unit: p ? Math.round(p.metrics?.harga_jual_efektif || 0) : '',
      potongan_persen: p && !NO_FEE.includes(form.saluran) ? p.potongan_default || 0 : 0,
    });
  };

  const pickSaluran = (s) => {
    const p = products.find((x) => x.id === form.product_id);
    setForm({ ...form, saluran: s, potongan_persen: NO_FEE.includes(s) ? 0 : (p?.potongan_default || 0) });
  };

  const qty = parseFloat(form.qty) || 0;
  const harga = parseFloat(form.harga_per_unit) || 0;
  const pot = parseFloat(form.potongan_persen) || 0;
  const hpp = selected?.metrics?.hpp_per_unit || 0;
  const omzet = qty * harga;
  const potonganTotal = (omzet * pot) / 100;
  const laba = omzet - potonganTotal - hpp * qty;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) { toast.error('Pilih produk dulu'); return; }
    try {
      await api.post('/sales', {
        product_id: form.product_id,
        qty,
        harga_per_unit: harga,
        potongan_persen: pot,
        tanggal: form.tanggal ? new Date(form.tanggal).toISOString() : null,
        saluran: form.saluran,
        catatan: form.catatan || null,
      });
      toast.success('Penjualan dicatat');
      setForm({ ...form, qty: 1, catatan: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mencatat penjualan');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={submit} data-testid="form-sale" className="lg:col-span-2 bg-card border border-border/60 rounded-xl p-5 lg:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <ShoppingBag size={24} weight="duotone" className="text-primary" />
          <h3 className="font-display text-lg font-semibold">Catat Penjualan</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1.5 sm:col-span-2">
            <span className="text-sm font-medium text-muted-foreground">Produk</span>
            <select value={form.product_id} onChange={(e) => pickProduct(e.target.value)} data-testid="sale-product"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40">
              <option value="">Pilih produk…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.nama} (stok {num(p.stok, 2)})</option>)}
            </select>
          </label>
          <Input label="Jumlah Terjual" type="number" value={form.qty} onChange={(v) => setForm({ ...form, qty: v })} testid="sale-qty" />
          <Input label="Harga per Unit" type="number" value={form.harga_per_unit} onChange={(v) => setForm({ ...form, harga_per_unit: v })} testid="sale-harga" />
          <Input label="Potongan Marketplace (%)" type="number" value={form.potongan_persen} onChange={(v) => setForm({ ...form, potongan_persen: v })} testid="sale-potongan" />
          <Input label="Tanggal" type="date" value={form.tanggal} onChange={(v) => setForm({ ...form, tanggal: v })} testid="sale-tanggal" />
          <label className="block space-y-1.5 sm:col-span-2">
            <span className="text-sm font-medium text-muted-foreground">Tempat Jual</span>
            <div className="flex flex-wrap gap-2">
              {SALURAN.map((s) => (
                <button type="button" key={s} onClick={() => pickSaluran(s)}
                  data-testid={`sale-saluran-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.saluran === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}>
                  {s}
                </button>
              ))}
            </div>
          </label>
          <div className="sm:col-span-2">
            <Input label="Catatan (opsional)" value={form.catatan} onChange={(v) => setForm({ ...form, catatan: v })} testid="sale-catatan" />
          </div>
        </div>
        <button type="submit" data-testid="btn-submit-sale" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90">Simpan Penjualan</button>
      </form>

      <aside className="bg-card border border-primary/30 rounded-xl p-5 lg:p-6 h-fit lg:sticky lg:top-24" data-testid="sale-preview">
        <h4 className="font-display font-semibold text-lg mb-4">Ringkasan Transaksi</h4>
        <div className="space-y-2.5 text-sm">
          <Row label="Omzet" value={rupiah(omzet)} />
          <Row label={`Potongan (${pot}%)`} value={`− ${rupiah(potonganTotal)}`} />
          <Row label={`HPP (${num(qty, 2)} × ${rupiah(hpp)})`} value={`− ${rupiah(hpp * qty)}`} />
          <div className="h-px bg-border my-2" />
          <Row label="Laba Bersih" value={<span data-testid="sale-laba" className={laba >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600'}>{rupiah(laba)}</span>} bold />
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold text-base' : ''}`}>{value}</span>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <input type={type} step="any" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
    </label>
  );
}
